import { formatReal } from '@cannect/utils/formatters'
import { DEFAULT_SHIPPING_COST } from '@cannect/utils/constants'
import { TProgressiveTier, TProgressiveTriggerType } from './types'

// min_quantity é apenas um alias de cn_progressive_discount_tier.min_threshold:
// quando rule.trigger_type === 'amount', o valor está em R$, não em unidades.

/**
 * Faixas exibíveis na PDP: descarta a faixa base sem nenhum benefício
 * (discount_percent = 0 no item E sem shipping_value no custo de importação), que representa
 * o preço cheio e não deve aparecer como benefício (FR-004). Mantém faixas de
 * regras 'shipping' (discount_percent sempre 0, benefício só em shipping_value).
 * NÃO calcula desconto — apenas filtra o que a API retornou.
 */
export const getDisplayableTiers = (tiers?: TProgressiveTier[]): TProgressiveTier[] => {
  if (!tiers?.length) return []
  return tiers.filter((tier) => Number(tier.discount_percent) > 0 || Number(tier.shipping_value) > 0)
}

/** Retorna true quando o trigger da regra é por valor monetário (R$), não por quantidade de unidades. */
export const isAmountTrigger = (triggerType?: string): boolean => triggerType === 'amount'

/**
 * Rótulo curto do limiar de uma faixa — "R$ X em produtos [da marca]" (amount) ou "X un" (quantity).
 * Use formatTierQuantityLabel quando precisar do sufixo "ou mais" para faixas abertas.
 */
export const formatTierThreshold = (
  tier: Pick<TProgressiveTier, 'min_quantity'>,
  triggerType?: TProgressiveTriggerType,
  options?: { brandScope?: boolean }
): string => {
  const n = Number(tier.min_quantity)
  if (isAmountTrigger(triggerType)) {
    const suffix = options?.brandScope ? ' em produtos da marca' : ' em produtos'
    return `${(formatReal(n) as string) || `R$ ${n}`}${suffix}`
  }
  return `${n} un`
}

/**
 * Rótulo do benefício de custo de importação de uma faixa.
 * shipping_discount_type === 'value': se shipping_value >= DEFAULT_SHIPPING_COST → "Custo de importação isento";
 * senão → "R$ X OFF no custo de importação".
 * percent: >= 100 → "Custo de importação isento"; senão → "X% OFF no custo de importação".
 * Sempre começa com maiúscula — a função abre chip ou frase.
 */
export const formatShippingTierLabel = (
  tier: Pick<TProgressiveTier, 'shipping_discount_type' | 'shipping_value'>
): string => {
  const v = Number(tier.shipping_value) || 0
  if (tier.shipping_discount_type === 'value') {
    return v >= DEFAULT_SHIPPING_COST
      ? 'Custo de importação isento'
      : `${(formatReal(v) as string) || `R$ ${v}`} OFF no custo de importação`
  }
  return v >= 100 ? 'Custo de importação isento' : `${tier.shipping_value}% OFF no custo de importação`
}

/**
 * Rótulo da coluna Frete da tabela de faixas: "Grátis" (100% percent ou value ≥ custo padrão),
 * "X%" / "R$ X" nos demais casos e "—" quando a faixa não tem benefício de frete.
 */
export const formatShippingColumnLabel = (
  tier: Pick<TProgressiveTier, 'shipping_discount_type' | 'shipping_value'>
): string => {
  const v = Number(tier.shipping_value) || 0
  if (v <= 0) return '—'
  if (tier.shipping_discount_type === 'value') {
    return v >= DEFAULT_SHIPPING_COST ? 'Grátis' : (formatReal(v) as string) || `R$ ${v}`
  }
  return v >= 100 ? 'Grátis' : `${v}%`
}

/** Aviso do teto de unidades com desconto ("Desconto válido até N un"); vazio quando a regra não tem teto. */
export const formatDiscountCapLabel = (maxDiscountedUnits?: number | null): string => {
  const n = Number(maxDiscountedUnits)
  return n > 0 ? `Desconto válido até ${n} un` : ''
}

/** Aviso de unidades sem desconto por causa do teto: "N un sem desconto (limite da promoção)"; vazio se N ≤ 0. */
export const formatFullPriceUnitsLabel = (fullPriceUnits?: number | null): string => {
  const n = Number(fullPriceUnits)
  return n > 0 ? `${n} un sem desconto (limite da promoção)` : ''
}

/**
 * Rótulo de quantidade da faixa.
 * amount: "R$ X ou mais em produtos [da marca]" / "R$ X em produtos [da marca]".
 * quantity: "X un ou mais" / "X un" para faixas abertas (open_ended).
 */
export const formatTierQuantityLabel = (
  tier: Pick<TProgressiveTier, 'min_quantity' | 'open_ended'>,
  triggerType?: TProgressiveTriggerType,
  options?: { brandScope?: boolean; capped?: boolean }
): string => {
  const n = Number(tier.min_quantity)
  if (isAmountTrigger(triggerType)) {
    const fmtR = (formatReal(n) as string) || `R$ ${n}`
    const suffix = options?.brandScope ? ' em produtos da marca' : ' em produtos'
    return tier.open_ended ? `${fmtR} ou mais${suffix}` : `${fmtR}${suffix}`
  }
  // Com teto a faixa não vale "ou mais": o desconto termina em max_discounted_units
  return tier.open_ended && !options?.capped ? `${n} un ou mais` : `${n} un`
}

/**
 * Valor total que determina em qual faixa o cliente está.
 * amount: soma em R$ (quantity × unitPrice). quantity: quantity em unidades.
 */
export const getTierTriggerTotal = ({
  quantity,
  unitPrice,
  triggerType
}: {
  quantity: number
  unitPrice: number
  triggerType?: TProgressiveTriggerType
}): number => (isAmountTrigger(triggerType) ? quantity * unitPrice : quantity)

/**
 * Retorna a maior faixa ativa para um dado triggerTotal.
 * Os tiers DEVEM estar ordenados crescentemente por min_quantity (como retorna a API).
 */
export const findActiveTier = <T extends Pick<TProgressiveTier, 'min_quantity'>>(
  sortedTiers: T[],
  triggerTotal: number
): T | undefined => sortedTiers.filter((t) => Number(t.min_quantity) <= triggerTotal).pop()

/**
 * Normaliza o desconto progressivo presente no payload do pedido para um total numérico.
 * Shape CONFIRMADO pela API (contratos 004): `progressive_discount` é um ARRAY de aplicações,
 * cada uma com `total_discount` (ou `null` quando não há desconto). Mantém fallback defensivo
 * para escalar / `{ total }` por segurança. Nunca recalcula nada — apenas soma o que a API devolveu.
 */
export const getProgressiveDiscountTotal = (
  progressiveDiscount?: number | { total?: number } | Array<{ total_discount?: number }> | null
): number => {
  if (progressiveDiscount == null) return 0
  if (typeof progressiveDiscount === 'number') return progressiveDiscount
  if (Array.isArray(progressiveDiscount)) {
    return progressiveDiscount.reduce((sum, app) => sum + Number(app?.total_discount ?? 0), 0)
  }
  return Number(progressiveDiscount.total ?? 0)
}

/**
 * Determina o rótulo da linha de desconto progressivo.
 * Quando alguma aplicação tem benefit_target='shipping', indica abatimento no custo de importação (FR-016).
 * Leitura defensiva — aceita o array confirmado ou shapes antigos.
 */
export const getProgressiveDiscountLabel = (progressiveDiscount?: unknown): string => {
  const SHIPPING_LABEL = 'Desconto progressivo (custo de importação)'
  const DEFAULT_LABEL = 'Desconto progressivo'
  if (!progressiveDiscount || typeof progressiveDiscount === 'number') return DEFAULT_LABEL
  if (Array.isArray(progressiveDiscount)) {
    return progressiveDiscount.some((app: any) => app?.benefit_target === 'shipping') ? SHIPPING_LABEL : DEFAULT_LABEL
  }
  const pd = progressiveDiscount as Record<string, any>
  if (pd.benefit_target === 'shipping') return SHIPPING_LABEL
  const apps: any[] = pd.applications ?? []
  return apps.some((app) => app?.benefit_target === 'shipping') ? SHIPPING_LABEL : DEFAULT_LABEL
}
